/**
 * Display-time violation filtering — keeps the violations panel scoped to
 * enabled jurisdictions. Outcome grouping (confirmed / needs-info / excepted)
 * is handled in ViolationsPanel via collapsible sections.
 */
import type { Violation } from "@vera/shared";

export interface ViolationFilterOptions {
  enabledJurisdictions: Record<string, boolean>;
}

export type ViolationOutcomeCategory =
  | "confirmed"
  | "user_verified"
  | "needs_info"
  | "excepted";

export interface ViolationOutcomeGroups {
  confirmed: Violation[];
  userVerified: Violation[];
  needsInfo: Violation[];
  excepted: Violation[];
}

export function jurisdictionIdOf(v: Violation): string | undefined {
  return v.provenance?.cite?.jurisdictionId;
}

/** Violations whose jurisdiction chip is currently enabled. */
export function filterByEnabledJurisdictions(
  violations: Violation[],
  enabled: Record<string, boolean>
): Violation[] {
  return violations.filter(v => {
    const jid = jurisdictionIdOf(v);
    return jid != null && !!enabled[jid];
  });
}

/** Panel filter: jurisdiction only (all outcome tiers stay visible). */
export function filterViolationsForDisplay(
  violations: Violation[],
  options: ViolationFilterOptions
): Violation[] {
  return filterByEnabledJurisdictions(violations, options.enabledJurisdictions);
}

export function categorizeViolation(v: Violation): ViolationOutcomeCategory {
  const status = v.provenance?.status;
  const userSupplied = (v.provenance?.userSupplied?.facts.length ?? 0) > 0;
  if (userSupplied && status === "confirmed_violation") {
    return "user_verified";
  }
  if (status === "confirmed_violation") return "confirmed";
  if (status === "compliant_via_exception") return "excepted";
  return "needs_info";
}

export function partitionViolationsByOutcome(
  violations: Violation[]
): ViolationOutcomeGroups {
  const groups: ViolationOutcomeGroups = {
    confirmed: [],
    userVerified: [],
    needsInfo: [],
    excepted: [],
  };
  for (const v of violations) {
    const cat = categorizeViolation(v);
    if (cat === "confirmed") groups.confirmed.push(v);
    else if (cat === "user_verified") groups.userVerified.push(v);
    else if (cat === "excepted") groups.excepted.push(v);
    else groups.needsInfo.push(v);
  }
  return groups;
}

/** Group violations by rule id so each collapsible shows one named rule. */
export function groupViolationsByRule(
  violations: Violation[]
): Array<{ ruleId: string; violations: Violation[] }> {
  const map = new Map<string, Violation[]>();
  for (const v of violations) {
    const ruleId = v.provenance?.ruleId ?? v.code;
    const list = map.get(ruleId) ?? [];
    list.push(v);
    map.set(ruleId, list);
  }
  return [...map.entries()]
    .sort(([, a], [, b]) => (a[0]?.code ?? "").localeCompare(b[0]?.code ?? ""))
    .map(([ruleId, items]) => ({ ruleId, violations: items }));
}
